import { isConvex, orderQuad, quadArea, squareness, type Point, type Quad } from './geometry';

/**
 * Findet die vier Ecken eines abfotografierten Fotos – das, was Scan-Apps
 * machen, nur ohne fremde Bibliothek und ohne WASM-Nachladen.
 *
 * Der Weg in fünf Schritten:
 *
 *  1. **Graustufen und leicht weichzeichnen.** Filmkorn und Bildrauschen
 *     erzeugen sonst Kanten, die es nicht gibt.
 *  2. **Sobel-Gradient.** Wo sich die Helligkeit ändert, zeigt `mag` einen
 *     Ausschlag und `dir` in welche Richtung – senkrecht zur Kante.
 *  3. **Hough-Transformation**, aber nur in Gradientenrichtung. Jeder
 *     Kantenpunkt stimmt für die Geraden ab, auf denen er liegen *könnte*;
 *     weil die Richtung schon bekannt ist, reichen ±2° statt aller 180°. Das
 *     ist rund 40-mal schneller und liefert deutlich sauberere Maxima.
 *  4. **Geraden gruppieren.** Ein Foto hat zwei Kantenpaare, die etwa 90°
 *     auseinanderliegen. Aus je zwei Geraden pro Gruppe entsteht ein Viereck.
 *  5. **Prüfen und bewerten.** Konvex? Winkel nahe 90°? Groß genug? Und –
 *     entscheidend – liegt entlang *jeder* der vier Seiten tatsächlich eine
 *     Kante? Die Hough-Stimmen gelten für die unendliche Gerade; ohne diese
 *     Nachprüfung würde eine Tischkante am Bildrand mitgezählt, obwohl das
 *     Foto ganz woanders liegt.
 *
 * Rückgabe sind normalisierte Koordinaten (0…1) in der Reihenfolge
 * TL, TR, BR, BL – oder `null`, wenn nichts überzeugt. Dann setzt der
 * Benutzer die Ecken selbst; ein falsch geratener Rahmen wäre schlimmer als
 * gar keiner.
 */

/** Schrittweite der Hough-Tabelle. 1° und 2 px sind für 480-px-Bilder genug. */
const THETA_STEPS = 180;
const RHO_STEP = 2;

/** Ab welchem Perzentil der Gradientenstärke ein Pixel als Kante gilt. */
const EDGE_PERCENTILE = 0.9;

/** Wie viele Geradenkandidaten weiterverfolgt werden. */
const MAX_LINES = 24;
const LINES_PER_GROUP = 6;
const REFERENCE_LINES = 4;

/** Halbe Breite des Suchbands quer zur Kante, in Pixeln des Analysebildes. */
const SUPPORT_BAND = 3;

/** Annahmeschwellen – unter diesen Werten gilt die Erkennung als gescheitert. */
const MIN_SIDE_SUPPORT = 0.45;
const MIN_SIDE_STEP = 0.55;
/** Ab welchem Helligkeitsunterschied (0…255) eine Stufe als Stufe gilt. */
const STEP_LEVELS = 6;
const MIN_SCORE = 0.3;
const MIN_AREA_FRACTION = 0.1;
const MIN_SQUARENESS = 0.4;

interface Line {
  /** Winkel der Normalen in Grad, 0…179. */
  theta: number;
  /** Abstand vom Ursprung entlang der Normalen, in Pixeln. */
  rho: number;
  votes: number;
}

interface Gradient {
  mag: Float32Array;
  dir: Float32Array;
  max: number;
}

export interface QuadDetection {
  quad: Quad;
  /**
   * 0…1 – welcher Anteil der vier Seiten auf einer echten Bildkante liegt.
   * Unter etwa 0,85 lohnt der Hinweis, die Ecken noch einmal anzusehen.
   */
  confidence: number;
}

function grayscale(image: ImageData): Float32Array {
  const out = new Float32Array(image.width * image.height);
  const px = image.data;
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = 0.299 * px[p]! + 0.587 * px[p + 1]! + 0.114 * px[p + 2]!;
  }
  return out;
}

/** 3×3-Mittelwert, separabel gerechnet. */
function blur(src: Float32Array, w: number, h: number): Float32Array {
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);

  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      const l = src[row + Math.max(0, x - 1)]!;
      const c = src[row + x]!;
      const r = src[row + Math.min(w - 1, x + 1)]!;
      tmp[row + x] = (l + c + r) / 3;
    }
  }
  for (let y = 0; y < h; y++) {
    const up = Math.max(0, y - 1) * w;
    const down = Math.min(h - 1, y + 1) * w;
    const row = y * w;
    for (let x = 0; x < w; x++) {
      out[row + x] = (tmp[up + x]! + tmp[row + x]! + tmp[down + x]!) / 3;
    }
  }
  return out;
}

function sobel(gray: Float32Array, w: number, h: number): Gradient {
  const mag = new Float32Array(gray.length);
  const dir = new Float32Array(gray.length);
  let max = 0;

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const tl = gray[i - w - 1]!;
      const t = gray[i - w]!;
      const tr = gray[i - w + 1]!;
      const l = gray[i - 1]!;
      const r = gray[i + 1]!;
      const bl = gray[i + w - 1]!;
      const b = gray[i + w]!;
      const br = gray[i + w + 1]!;

      const gx = tr + 2 * r + br - (tl + 2 * l + bl);
      const gy = bl + 2 * b + br - (tl + 2 * t + tr);
      const m = Math.sqrt(gx * gx + gy * gy);

      mag[i] = m;
      dir[i] = Math.atan2(gy, gx);
      if (m > max) max = m;
    }
  }
  return { mag, dir, max };
}

/** Schwelle als Perzentil über ein 256-Fächer-Histogramm. */
function edgeThreshold(grad: Gradient, fraction: number): number {
  if (grad.max <= 0) return Infinity;
  const hist = new Int32Array(256);
  let count = 0;
  for (let i = 0; i < grad.mag.length; i++) {
    const m = grad.mag[i]!;
    if (m <= 0) continue;
    hist[Math.min(255, Math.floor((m / grad.max) * 255))]!++;
    count++;
  }
  if (count === 0) return Infinity;

  const target = count * fraction;
  let acc = 0;
  for (let b = 0; b < 256; b++) {
    acc += hist[b]!;
    if (acc >= target) return (b / 255) * grad.max;
  }
  return grad.max;
}

interface Hough {
  acc: Float32Array;
  rhoBins: number;
  rhoMax: number;
}

function houghTransform(grad: Gradient, w: number, h: number, threshold: number): Hough {
  const rhoMax = Math.hypot(w, h);
  const rhoBins = Math.ceil((2 * rhoMax) / RHO_STEP) + 1;
  const acc = new Float32Array(THETA_STEPS * rhoBins);

  const cos = new Float32Array(THETA_STEPS);
  const sin = new Float32Array(THETA_STEPS);
  for (let t = 0; t < THETA_STEPS; t++) {
    const rad = (t * Math.PI) / 180;
    cos[t] = Math.cos(rad);
    sin[t] = Math.sin(rad);
  }

  const spread = 2;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const m = grad.mag[i]!;
      if (m < threshold) continue;

      // Die Normale einer Kante ist der Gradient; θ ist also direkt dessen
      // Winkel, modulo 180° (Vorzeichen steckt anschließend in ρ).
      const base = ((Math.round((grad.dir[i]! * 180) / Math.PI) % 180) + 180) % 180;
      for (let d = -spread; d <= spread; d++) {
        const t = (((base + d) % 180) + 180) % 180;
        const rho = x * cos[t]! + y * sin[t]!;
        const bin = Math.round((rho + rhoMax) / RHO_STEP);
        acc[t * rhoBins + bin]! += m;
      }
    }
  }

  return { acc, rhoBins, rhoMax };
}

/**
 * Zwei Geraden gelten als dieselbe, wenn Winkel und Abstand nahe beieinander
 * liegen. Am Übergang 179°/0° kippt bei θ das Vorzeichen von ρ – deshalb wird
 * ρ beim Umschlag mitgedreht, sonst würde dieselbe Kante doppelt gezählt.
 */
function sameLine(a: Line, b: Line): boolean {
  let dt = a.theta - b.theta;
  let rho = b.rho;
  if (dt > 90) {
    dt -= 180;
    rho = -rho;
  } else if (dt < -90) {
    dt += 180;
    rho = -rho;
  }
  return Math.abs(dt) <= 4 && Math.abs(a.rho - rho) <= 14;
}

/** Stärkste Geraden, gierig ausgewählt und gegen Dubletten gefiltert. */
function strongestLines(hough: Hough): Line[] {
  const { acc, rhoBins, rhoMax } = hough;

  let max = 0;
  for (let i = 0; i < acc.length; i++) if (acc[i]! > max) max = acc[i]!;
  if (max <= 0) return [];

  const floor = max * 0.12;
  const candidates: Line[] = [];
  for (let t = 0; t < THETA_STEPS; t++) {
    for (let bin = 0; bin < rhoBins; bin++) {
      const votes = acc[t * rhoBins + bin]!;
      if (votes < floor) continue;
      candidates.push({ theta: t, rho: bin * RHO_STEP - rhoMax, votes });
    }
  }
  candidates.sort((a, b) => b.votes - a.votes);

  const chosen: Line[] = [];
  for (const line of candidates) {
    if (chosen.length >= MAX_LINES) break;
    if (chosen.some((other) => sameLine(line, other))) continue;
    chosen.push(line);
  }
  return chosen;
}

/** Winkelabstand zweier Normalenrichtungen, 0…90°. */
function angleGap(a: number, b: number): number {
  const d = Math.abs(a - b) % 180;
  return d > 90 ? 180 - d : d;
}

function intersect(a: Line, b: Line): Point | null {
  const ra = (a.theta * Math.PI) / 180;
  const rb = (b.theta * Math.PI) / 180;
  const ca = Math.cos(ra);
  const sa = Math.sin(ra);
  const cb = Math.cos(rb);
  const sb = Math.sin(rb);

  const det = ca * sb - cb * sa;
  if (Math.abs(det) < 1e-6) return null;

  return {
    x: (a.rho * sb - b.rho * sa) / det,
    y: (b.rho * ca - a.rho * cb) / det,
  };
}

/**
 * Anteil der Strecke, an der die Helligkeit *dauerhaft* umschlägt.
 *
 * Warum das zusätzlich zur Kantendeckung nötig ist: eine Holzmaserung, eine
 * Tischfuge oder eine Falte im Tischtuch ist eine perfekte Gerade mit
 * kräftigem Gradienten – aber links und rechts davon ist es gleich hell. Eine
 * echte Fotokante trennt zwei verschieden helle Flächen, und zwar mit
 * gleichem Vorzeichen über die ganze Seite hinweg. Ohne diese Prüfung legt
 * der Detektor den Rahmen gern auf die stärkste Maserungslinie statt auf ein
 * kontrastarmes Foto.
 *
 * Gemessen wird in *zwei* Abständen (nah und fern). Eine echte Flächengrenze
 * behält ihr Vorzeichen, egal wie weit man sich von ihr entfernt. Ein
 * Streifenmuster nicht: dort kippt der Unterschied schon nach einer halben
 * Wellenlänge. Genau daran scheitert sonst ein dunkles Foto auf einer stark
 * geriffelten Tischplatte – die Maserung gewinnt gegen die schwache Kante.
 */
function sideStep(
  a: Point,
  b: Point,
  center: Point,
  gray: Float32Array,
  w: number,
  h: number,
): number {
  const samples = 32;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return 0;

  // Normale so wählen, dass sie vom Schwerpunkt weg zeigt.
  let nx = -dy / len;
  let ny = dx / len;
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  if ((mx - center.x) * nx + (my - center.y) * ny < 0) {
    nx = -nx;
    ny = -ny;
  }

  const near = 4;
  const far = 9;
  const pairs: Array<[number, number]> = [];
  for (let s = 0; s < samples; s++) {
    const t = (s + 0.5) / samples;
    const px = a.x + dx * t;
    const py = a.y + dy * t;

    const at = (distance: number): number | null => {
      const x = Math.round(px + nx * distance);
      const y = Math.round(py + ny * distance);
      if (x < 0 || y < 0 || x >= w || y >= h) return null;
      return gray[y * w + x]!;
    };

    const outNear = at(near);
    const inNear = at(-near);
    const outFar = at(far);
    const inFar = at(-far);
    if (outNear === null || inNear === null || outFar === null || inFar === null) continue;

    pairs.push([outNear - inNear, outFar - inFar]);
  }
  if (pairs.length < samples * 0.6) return 0;

  const mean = pairs.reduce((sum, [n]) => sum + n, 0) / pairs.length;
  if (Math.abs(mean) < STEP_LEVELS) return 0;

  const sign = Math.sign(mean);
  const agreeing = pairs.filter(
    ([n, f]) =>
      Math.sign(n) === sign &&
      Math.abs(n) >= STEP_LEVELS &&
      Math.sign(f) === sign &&
      Math.abs(f) >= STEP_LEVELS,
  ).length;
  return agreeing / pairs.length;
}

/**
 * Anteil der Strecke, an der auch wirklich eine Kante liegt.
 *
 * Gesucht wird senkrecht zur Strecke in einem schmalen Band, denn die
 * Hough-Gerade sitzt selten pixelgenau: der Winkel ist auf 1° gerastert, und
 * über eine Bildbreite hinweg läuft das schon ein paar Pixel auseinander. Zu
 * eng gewählt, bestraft das Band gedrehte Aufnahmen ohne Grund. Seiten, die
 * überwiegend außerhalb des Bildes verlaufen, zählen als 0.
 */
function sideSupport(
  a: Point,
  b: Point,
  grad: Gradient,
  w: number,
  h: number,
  threshold: number,
): number {
  const samples = 64;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return 0;

  const nx = -dy / len;
  const ny = dx / len;

  let hits = 0;
  let used = 0;
  for (let s = 0; s < samples; s++) {
    const t = (s + 0.5) / samples;
    const px = a.x + dx * t;
    const py = a.y + dy * t;

    let best = 0;
    let inside = false;
    for (let d = -SUPPORT_BAND; d <= SUPPORT_BAND; d++) {
      const x = Math.round(px + nx * d);
      const y = Math.round(py + ny * d);
      if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
      inside = true;
      const m = grad.mag[y * w + x]!;
      if (m > best) best = m;
    }
    if (!inside) continue;
    used++;
    if (best >= threshold) hits++;
  }

  if (used < samples * 0.6) return 0;
  return hits / used;
}

interface Scored {
  quad: Quad;
  /** Nur für die Auswahl unter den Kandidaten. */
  score: number;
  /** Anteil der vier Seiten, der von echten Kanten gedeckt ist. */
  coverage: number;
}

function evaluate(
  points: Point[],
  grad: Gradient,
  gray: Float32Array,
  w: number,
  h: number,
  threshold: number,
): Scored | null {
  // Ein Stück über den Bildrand hinaus ist erlaubt: die Hough-Geraden treffen
  // sich nicht immer exakt dort, wo die Ecke sichtbar endet.
  const margin = 0.03;
  if (
    points.some(
      (p) => p.x < -margin * w || p.x > (1 + margin) * w || p.y < -margin * h || p.y > (1 + margin) * h,
    )
  ) {
    return null;
  }

  const quad = orderQuad(points);
  if (!quad || !isConvex(quad)) return null;

  const areaFraction = quadArea(quad) / (w * h);
  if (areaFraction < MIN_AREA_FRACTION || areaFraction > 1.1) return null;

  const square = squareness(quad);
  if (square < MIN_SQUARENESS) return null;

  const shortest = Math.min(
    Math.hypot(quad[1].x - quad[0].x, quad[1].y - quad[0].y),
    Math.hypot(quad[2].x - quad[1].x, quad[2].y - quad[1].y),
    Math.hypot(quad[3].x - quad[2].x, quad[3].y - quad[2].y),
    Math.hypot(quad[0].x - quad[3].x, quad[0].y - quad[3].y),
  );
  if (shortest < 0.15 * Math.min(w, h)) return null;

  const center = {
    x: (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4,
    y: (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4,
  };

  let supportTotal = 0;
  let stepTotal = 0;
  for (let i = 0; i < 4; i++) {
    const a = quad[i]!;
    const b = quad[(i + 1) % 4]!;

    const support = sideSupport(a, b, grad, w, h, threshold);
    if (support < MIN_SIDE_SUPPORT) return null;
    supportTotal += support;

    const step = sideStep(a, b, center, gray, w, h);
    if (step < MIN_SIDE_STEP) return null;
    stepTotal += step;
  }

  const coverage = supportTotal / 4;

  // In die Auswahl geht mehr ein als in die gemeldete Sicherheit: die
  // Helligkeitsstufe, die Größe (sonst gewinnt der Bilderrahmen *im* Foto
  // gegen das Foto selbst) und die Rechtwinkligkeit.
  const score =
    coverage *
    (0.6 + 0.4 * (stepTotal / 4)) *
    (0.4 + 0.6 * Math.min(1, areaFraction)) *
    (0.5 + 0.5 * square);

  return { quad, score, coverage };
}

export function detectPhotoQuad(image: ImageData): QuadDetection | null {
  const w = image.width;
  const h = image.height;
  if (w < 32 || h < 32) return null;

  const gray = blur(grayscale(image), w, h);
  const grad = sobel(gray, w, h);
  const threshold = edgeThreshold(grad, EDGE_PERCENTILE);
  if (!Number.isFinite(threshold)) return null;

  const lines = strongestLines(houghTransform(grad, w, h, threshold));
  if (lines.length < 4) return null;

  let best: Scored | null = null;

  // Welche Geraden „waagerecht" und welche „senkrecht" sind, hängt davon ab,
  // wie schief das Foto liegt. Deshalb wird der Bezugswinkel nicht geraten,
  // sondern es werden die stärksten Geraden der Reihe nach durchprobiert.
  for (const reference of lines.slice(0, REFERENCE_LINES)) {
    const groupA: Line[] = [];
    const groupB: Line[] = [];
    for (const line of lines) {
      const gap = angleGap(line.theta, reference.theta);
      if (gap <= 35) groupA.push(line);
      else if (gap >= 55) groupB.push(line);
    }
    if (groupA.length < 2 || groupB.length < 2) continue;

    const a = groupA.slice(0, LINES_PER_GROUP);
    const b = groupB.slice(0, LINES_PER_GROUP);

    for (let i = 0; i < a.length; i++) {
      for (let j = i + 1; j < a.length; j++) {
        for (let k = 0; k < b.length; k++) {
          for (let l = k + 1; l < b.length; l++) {
            const corners = [
              intersect(a[i]!, b[k]!),
              intersect(a[i]!, b[l]!),
              intersect(a[j]!, b[k]!),
              intersect(a[j]!, b[l]!),
            ];
            if (corners.some((p) => p === null)) continue;

            const scored = evaluate(corners as Point[], grad, gray, w, h, threshold);
            if (scored && (!best || scored.score > best.score)) best = scored;
          }
        }
      }
    }
  }

  if (!best || best.score < MIN_SCORE) return null;

  return {
    quad: best.quad.map((p) => ({ x: p.x / w, y: p.y / h })) as Quad,
    confidence: best.coverage,
  };
}
